import { Box, SelectChangeEvent } from '@mui/material'
import React from 'react'
import { appContext } from '../../../AppContext';
import IbssTextField from '../../Inputs/TextField/IbssTextField';
import { DateTime } from 'luxon';
import { IbssComponent } from '../../Core/BaseComponent/IbssComponent';
import { DateHelper } from '../../../Common/DateHelper';
import UserPicker, { IUser } from '../../Inputs/UserPicker/UserPicker';
import { VisitorField, VisitorFieldHelper } from './Visitor';
import IbssDatePicker from '../../Inputs/DatePicker/IbssDatePicker';
import IbssTimePicker from '../../Inputs/TimePicker/IbssTimePicker';

class EditHostDetails extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private get hasStartOrEndChanged() { return this.state.hasStartChanged || this.state.hasEndChanged; }
    private fieldHelper: VisitorFieldHelper;

    constructor(props: IProps)
    {
        super(props);
        this.fieldHelper = new VisitorFieldHelper(props.fields);
        this.state = {
            host: null,
            hasHostNameChanged: false,
            hasStartChanged: false,
            hasEndChanged: false,
        };
    }

    public resetValidation(): void
    {
        this.setState({
            hasHostNameChanged: false,
            hasStartChanged: false,
            hasEndChanged: false,
        });
    }

    public componentDidMount(): void
    {
        this.props.onValidate(this.isValid());
    }

    public componentDidUpdate(prevProps: IProps, prevState: IState): void
    {
        if (this.isValid(prevProps, prevState) != this.isValid())
        {
            this.props.onValidate(this.isValid());
        }
    }

    private handleHostEmailChange(value: string): void
    {
        this.setState({ hasHostNameChanged: true });
        this.props.onHostEmailChange(value);
    }

    private handleHostChange(host: IUser | null): void
    {
        this.setState({ host: host });
        this.props.onHostChange(host);
    }

    private handleOnDateChange(value: DateTime | null): void
    {
        if (value)
        {
            const newStart = this.props.start?.set({ year: value.year, month: value.month, day: value.day });
            const newEnd = this.props.end?.set({ year: value.year, month: value.month, day: value.day });
            if (newStart) this.props.onStartChange(newStart);
            if (newEnd) this.props.onEndChange(newEnd);
        }
    }

    private handleStartChange(value: DateTime)
    {
        this.setState({ hasStartChanged: true });
        this.props.onStartChange(value);
    }

    private handleEndChange(value: DateTime)
    {
        this.setState({ hasEndChanged: true });
        this.props.onEndChange(value);
    }

    private isValid(props?: IProps, state?: IState) { return this.isHostValid(state) && this.isStartValid(props) && this.isEndValid(props) && this.areTimesValid(props); }
    private isHostValid(state?: IState) { return (!this.fieldHelper.has(VisitorField.Host) || ((state ?? this.state).host != null)); }
    private isStartValid(props?: IProps) { return (!this.fieldHelper.has(VisitorField.Times) || ((props ?? this.props).start?.isValid ?? false)); }
    private isEndValid(props?: IProps) { return (!this.fieldHelper.has(VisitorField.Times) || ((props ?? this.props).end?.isValid ?? false)); }

    private areTimesValid(props?: IProps): boolean
    {
        if (!this.fieldHelper.has(VisitorField.Times))
        {
            return true;
        }
        const start = (props ?? this.props).start;
        const end = (props ?? this.props).end;

        if (!start || !end)
        {
            return false;
        }
        const isValid = (start.isValid && end.isValid && start < end);
        return isValid;
    }

    private get startHelperText(): string
    {
        const start = this.props.start;
        const end = this.props.end;

        if (!start || !end)
        {
            return '';
        }
        else if (start.isNull())
        {
            return this.labels.funcCateringMenuEditRequired_S;
        }
        else if (start.isValid && end.isValid && start >= end)
        {
            return this.labels.funcAnnouncementStartTimeError_Message; 
        }
        else
        {
            return '';
        }
    }

    private get endHelperText(): string
    {
        const start = this.props.start;
        const end = this.props.end;

        if (!start || !end)
        {
            return '';
        }
        else if (start.isValid && end.isValid && start >= end)
        {
            return this.labels.funcAnnouncementEndError_Message; 
        }
        else
        {
            return '';
        }
    }

    public render(): JSX.Element
    {
        const startError = ((this.state.hasStartChanged && !this.isStartValid()) || (this.hasStartOrEndChanged && !this.areTimesValid()));
        const endError = ((this.state.hasEndChanged && !this.isEndValid()) || (this.hasStartOrEndChanged && !this.areTimesValid()));

        return (
            <div>
                {
                    this.fieldHelper.has(VisitorField.Host) && this.fieldHelper.has(VisitorField.ArrivalLocation) &&
                    <h3>{this.labels.funcHostDetails_S}</h3>
                }
                {
                    this.fieldHelper.has(VisitorField.Host) &&
                    <>
                        <UserPicker
                            searchText={this.props.hostEmail ?? ''}
                            placeholder={this.labels.HubLabelHostName}
                            onChange={async searchText => this.handleHostEmailChange(searchText)}
                            onUserChange={async host => this.handleHostChange(host)}
                            error={this.state.hasHostNameChanged && !this.isHostValid()}
                            helperText={this.state.hasHostNameChanged && !this.isHostValid() ? this.labels.funcProvideAHost_L : ''}
                            disabled={this.props.disableFields}
                        />
                        <IbssTextField
                            fullWidth
                            required
                            name="hostName"
                            label={this.labels.HubLabelHost}
                            value={this.state.host?.displayName ?? ''}
                            variant="outlined"
                            className='mt-3'
                            disabled
                        />
                    </>
                }
                {
                    (this.fieldHelper.has(VisitorField.Host) || this.fieldHelper.has(VisitorField.ArrivalLocation)) && this.fieldHelper.has(VisitorField.Times) &&
                    <hr />
                }
                {
                    this.fieldHelper.has(VisitorField.Times) &&
                    <>
                        <h3>{this.labels.funcTimings_S}</h3>
                        <Box display={'flex'} sx={{ '& > :not(style)': { my: 1, mr: 1 }, }}>
                            <IbssDatePicker
                                label={this.labels.HubLabelDate}
                                value={this.props.start}
                                minDate={DateHelper.now(this.props.buildingId)}
                                onChange={(e) => this.handleOnDateChange(e)}
                                disabled={this.props.disableFields}
                                slotProps={{textField:{error: startError}}}
                            />
                            <IbssTimePicker
                                label={this.labels.HubLabelVisitStart}
                                value={this.props.start}
                                onChange={(e) => e && this.handleStartChange(e)}
                                minTime={(this.props.start?.hasSame(DateHelper.now(this.props.buildingId), 'day') ?? false) ? DateHelper.now(this.props.buildingId) : undefined}
                                slotProps={{
                                    textField:
                                    {
                                        error: startError,
                                        helperText: this.startHelperText,
                                    }
                                }}
                                disabled={this.props.disableFields}
                            />
                            <IbssTimePicker
                                label={this.labels.HubLabelVisitEnd}
                                value={this.props.end}
                                onChange={e => e && this.handleEndChange(e)}
                                minTime={this.props.start}
                                slotProps={{
                                    textField:
                                    {
                                        error: endError,
                                        helperText: this.endHelperText,
                                    }
                                }}
                                disabled={this.props.disableFields}
                            />
                        </Box>
                    </>
                }
            </div>
        );
    }
}

export default EditHostDetails;

export interface IProps
{
    fields?: VisitorField;
    buildingId: number;
    hostEmail?: string;
    start?: DateTime;
    end?: DateTime;
    onValidate: (isValid: boolean) => void;
    onHostEmailChange: (value: string) => void;
    onHostChange: (value: IUser | null) => void;
    onStartChange: (event: DateTime) => void;
    onEndChange: (event: DateTime) => void;
    disableFields?: boolean
}

export interface IFilterOptions
{
    label: string;
    value: string;
}

interface IState
{
    host: IUser | null;
    hasHostNameChanged: boolean;
    hasStartChanged: boolean;
    hasEndChanged: boolean;
}
